// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

// Language selection with two-layer persistence:
//  - Always: switch the live UI (i18next) and save the choice to localStorage so
//    it loads instantly next time, before the encrypted log is available.
//  - When the ledger is unlocked: also write it to the encrypted `language`
//    setting so it syncs across devices (see hooks/useSettings.ts).
// The reverse direction — a synced value winning on load — is handled by the
// reconciliation effect in pages/MainApp.tsx.

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../store/AppContext.js';
import { useSettings } from './useSettings.js';
import { setStoredLanguage } from '../i18n/language.js';
import { LOCALES, SUPPORTED_LOCALE_CODES } from '../i18n/locales.js';

export function useLanguage(worker: Worker | null) {
  const { i18n } = useTranslation();
  const { state } = useApp();
  const { updateSetting } = useSettings(worker);
  const { auth } = state;

  const change = useCallback(
    async (code: string): Promise<void> => {
      if (!SUPPORTED_LOCALE_CODES.includes(code) || code === i18n.language) return;
      // Live switch + instant local persistence (config.ts syncs document lang,
      // font script and the display locale off the languageChanged event).
      await i18n.changeLanguage(code);
      setStoredLanguage(code);
      // Sync into the encrypted settings event only once the ledger is unlocked;
      // pre-auth (e.g. the login screen) the local choice is enough.
      if (worker && auth.isAuthenticated && auth.keyData) {
        try {
          await updateSetting('language', code);
        } catch {
          // Offline / transient: the local choice still applies and will sync later.
        }
      }
    },
    [i18n, worker, auth.isAuthenticated, auth.keyData, updateSetting],
  );

  return { current: i18n.language, change, locales: LOCALES };
}
